/**
 * De PUT naar de opslag.
 *
 * `XMLHttpRequest` en niet `fetch`, om precies één reden: fetch geeft geen
 * voortgang van een upload. Zonder dat kan het uploadstatus-scherm alleen
 * "bezig" zeggen en nooit "68%", en dat is het halve scherm.
 */

export interface PutResult {
  /** De ETag die de opslag teruggeeft; nodig om een multipart af te ronden. */
  etag: string | null
}

export class UploadAborted extends Error {
  constructor() {
    super('De upload is afgebroken.')
    this.name = 'UploadAborted'
  }
}

export function putToStorage(
  url: string,
  body: Blob,
  headers: Record<string, string>,
  options: {
    signal?: AbortSignal
    onProgress?: (sentBytes: number) => void
  } = {},
): Promise<PutResult> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest()

    request.open('PUT', url, true)

    // Exact de headers die de API meegaf en verder niets: ze zitten in de
    // handtekening, dus een extra header maakt de PUT ongeldig.
    for (const [name, value] of Object.entries(headers)) {
      request.setRequestHeader(name, value)
    }

    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        options.onProgress?.(event.loaded)
      }
    }

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve({ etag: normalizeEtag(request.getResponseHeader('ETag')) })

        return
      }

      reject(new Error(`De opslag weigerde het bestand (${request.status}).`))
    }

    request.onerror = () => reject(new Error('De verbinding viel weg.'))
    request.ontimeout = () => reject(new Error('De opslag reageerde niet op tijd.'))
    request.onabort = () => reject(new UploadAborted())

    if (options.signal) {
      if (options.signal.aborted) {
        reject(new UploadAborted())

        return
      }

      options.signal.addEventListener('abort', () => request.abort(), { once: true })
    }

    request.send(body)
  })
}

/**
 * S3 geeft de ETag met aanhalingstekens eromheen terug. Bij het afronden van
 * een multipart moet hij er precies zo weer in, dus we normaliseren naar de
 * kale hash en zetten ze aan de serverkant terug.
 */
function normalizeEtag(raw: string | null): string | null {
  return raw ? raw.replace(/^"|"$/g, '') : null
}
